<template>
  <div class="container-fluid mt-3 gmail-inbox-page">
    <div class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2 gmail-topbar">
      <div class="d-flex align-items-baseline gap-2 flex-wrap">
        <h2 class="h5 mb-0"><i class="bi bi-envelope-check me-1"></i>Bandeja a 0</h2>
        <span v-if="status?.connected" class="small text-muted">
          {{ status.email || 'Gmail' }}
          <span v-if="status.agentMode"> · {{ etiquetaAgente }}</span>
          <span v-if="status.push" class="ms-1"> · {{ etiquetaPush }}</span>
        </span>
      </div>
      <div class="d-flex gap-2 flex-wrap">
        <button
          v-if="!status?.connected"
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="conectando || !status?.oauthConfigured"
          @click="conectar"
        >
          <span v-if="conectando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-google me-1"></i>
          Conectar Gmail
        </button>
        <template v-else>
          <router-link to="/seguimiento" class="btn btn-outline-secondary btn-sm">
            <i class="bi bi-truck me-1"></i>Seguimiento
          </router-link>
          <button
            type="button"
            class="btn btn-sm"
            :class="pausaActiva ? 'btn-warning' : 'btn-outline-warning'"
            :disabled="cambiandoPausa || cargando"
            :title="pausaActiva
              ? 'Pausa activa: los correos nuevos van a la cola (etiqueta Cola-pausa). Pulsa para reanudar y recibirlos.'
              : 'Pausar la bandeja: los correos nuevos irán a una cola hasta que reanudes.'"
            :aria-pressed="pausaActiva"
            @click="alternarPausa"
          >
            <span v-if="cambiandoPausa" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else :class="pausaActiva ? 'bi bi-play-fill me-1' : 'bi bi-pause-fill me-1'"></i>
            {{ pausaActiva ? 'Reanudar bandeja' : 'Pausar bandeja' }}
          </button>
          <button
            type="button"
            class="btn btn-sm"
            :class="horarioActivo ? 'btn-secondary' : 'btn-outline-secondary'"
            title="Horario automático y remitentes que nunca se pausan"
            @click="abrirHorario"
          >
            <i class="bi bi-sliders me-1"></i>Ajustes pausa<span v-if="horarioActivo"> ✓</span>
          </button>
          <button type="button" class="btn btn-outline-primary btn-sm" :disabled="cargando" @click="cargarInbox">
            <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-arrow-clockwise me-1"></i>
            Actualizar
          </button>
          <button
            type="button"
            class="btn btn-success btn-sm"
            :disabled="aplicando || !counts.auto"
            @click="aplicarSeguras"
          >
            <span v-if="aplicando" class="spinner-border spinner-border-sm me-1"></span>
            <i v-else class="bi bi-magic me-1"></i>
            {{ counts.auto || 0 }} seguras
          </button>
          <button type="button" class="btn btn-outline-danger btn-sm" :disabled="cargando" @click="desconectar">
            Desconectar
          </button>
        </template>
      </div>
    </div>

    <div v-if="status?.connected && pausaActiva" class="alert alert-warning py-2 d-flex align-items-center gap-2" role="status">
      <i class="bi bi-pause-circle-fill"></i>
      <span>
        Bandeja en pausa: los correos nuevos se guardan en la cola
        <span v-if="pausaDesde"> desde las <strong>{{ horaPausa }}</strong></span>
        <span v-if="pausaHasta"> hasta las <strong>{{ horaFinPausa }}</strong> (fin del tramo del horario, se reanuda sola)</span>
        <span v-else-if="status?.pausa?.origen === 'manual'"> hasta que la reanudes</span>
        y aparecerán en la bandeja al reanudar.
      </span>
    </div>

    <div
      v-if="status?.connected && !pausaActiva && status?.pausa?.horarioEstado?.enTramo"
      class="alert alert-info py-2 d-flex align-items-center gap-2 flex-wrap"
      role="status"
    >
      <i class="bi bi-info-circle-fill"></i>
      <span>
        Ahora mismo estás dentro de un tramo del horario ({{ formatearHora(status.pausa.horarioEstado.desde) }}–{{ formatearHora(status.pausa.horarioEstado.hasta) }})
        pero la bandeja <strong>no está pausada</strong>:
        <template v-if="status.pausa.horarioEstado.omitidaHasta">
          la reanudaste a mano y no se volverá a pausar hasta que acabe este tramo.
        </template>
        <template v-else-if="status.pausa.horarioEstado.error">
          el servidor no ha podido activarla ({{ status.pausa.horarioEstado.error }}).
        </template>
        <template v-else>
          el servidor la activará en unos segundos; pulsa Actualizar si no cambia.
        </template>
      </span>
    </div>

    <div v-if="status?.connected && mostrarHorario" class="card card-body mb-3 gmail-horario">
      <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div class="form-check form-switch mb-0">
          <input id="horarioActivo" v-model="horarioForm.enabled" class="form-check-input" type="checkbox" />
          <label class="form-check-label fw-semibold" for="horarioActivo">
            Pausar automáticamente en estos horarios
          </label>
        </div>
        <span class="small text-muted">Zona horaria: {{ horarioForm.tz }}</span>
      </div>
      <p class="small text-muted mb-2">
        Dentro de cada tramo la bandeja se pausa sola y al terminar se reanuda (los correos de la cola
        vuelven a la bandeja). Si reanudas a mano durante un tramo, no se vuelve a pausar hasta el siguiente.
      </p>
      <div v-for="dia in diasSemana" :key="dia.idx" class="d-flex align-items-start gap-2 mb-2 flex-wrap">
        <div class="fw-semibold" style="width: 6.5rem">{{ dia.nombre }}</div>
        <div class="d-flex flex-column gap-1">
          <div v-for="(t, i) in horarioForm.dias[dia.idx]" :key="i" class="d-flex align-items-center gap-1">
            <input v-model="t.desde" type="time" class="form-control form-control-sm" style="width: 7rem" />
            <span>–</span>
            <input v-model="t.hasta" type="time" class="form-control form-control-sm" style="width: 7rem" />
            <button type="button" class="btn btn-outline-danger btn-sm" title="Quitar tramo" @click="quitarTramo(dia.idx, i)">
              <i class="bi bi-x-lg"></i>
            </button>
          </div>
          <div v-if="!horarioForm.dias[dia.idx].length" class="small text-muted">Sin pausa</div>
        </div>
        <div class="ms-auto d-flex gap-1">
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="anadirTramo(dia.idx)">
            <i class="bi bi-plus-lg"></i> Tramo
          </button>
          <button
            v-if="dia.idx === 1"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            title="Copiar los tramos del lunes a martes-viernes"
            @click="copiarTramos(1, [2, 3, 4, 5])"
          >
            Copiar a L-V
          </button>
          <button
            v-if="dia.idx === 1"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            title="Copiar los tramos del lunes a sábado y domingo"
            @click="copiarTramos(1, [6, 0])"
          >
            Copiar a fin de semana
          </button>
          <button
            v-if="dia.idx === 6"
            type="button"
            class="btn btn-outline-secondary btn-sm"
            title="Copiar los tramos del sábado al domingo"
            @click="copiarTramos(6, [0])"
          >
            Copiar a domingo
          </button>
        </div>
      </div>
      <hr class="my-3" />
      <div class="fw-semibold mb-1">Remitentes que nunca se pausan</div>
      <p class="small text-muted mb-2">
        Sus correos entran directamente en la bandeja aunque la pausa esté activa (y avisan como siempre).
        Añade un email (<code>ana@empresa.com</code>) o un dominio (<code>empresa.com</code>).
        No afecta a los correos que ya estén en la cola.
      </p>
      <form class="d-flex gap-2 mb-2" @submit.prevent="anadirExcepcion">
        <input
          v-model="nuevaExcepcion"
          type="text"
          class="form-control form-control-sm"
          placeholder="ana@empresa.com o empresa.com"
          maxlength="120"
          style="max-width: 22rem"
        />
        <button type="submit" class="btn btn-outline-primary btn-sm" :disabled="!nuevaExcepcion.trim() || guardandoExcepcion">
          <i class="bi bi-plus-lg"></i> Añadir
        </button>
      </form>
      <div class="d-flex flex-wrap gap-2 mb-1">
        <span v-for="ex in excepciones" :key="ex.id" class="badge text-bg-light border d-inline-flex align-items-center gap-1">
          <i :class="ex.tipo === 'dominio' ? 'bi bi-globe2' : 'bi bi-person'"></i>{{ ex.valor }}
          <button type="button" class="btn-close ms-1" style="font-size: .6rem" :aria-label="`Quitar ${ex.valor}`" @click="quitarExcepcion(ex.id)"></button>
        </span>
        <span v-if="!excepciones.length" class="small text-muted">Ninguno: todos los remitentes se pausan.</span>
      </div>
      <div class="d-flex gap-2 mt-2">
        <button type="button" class="btn btn-primary btn-sm" :disabled="guardandoHorario" @click="guardarHorario">
          <span v-if="guardandoHorario" class="spinner-border spinner-border-sm me-1"></span>
          Guardar horario
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="mostrarHorario = false">Cerrar</button>
      </div>
    </div>

    <div v-if="mensajeOAuth" class="alert py-2" :class="mensajeOAuthOk ? 'alert-success' : 'alert-danger'">
      {{ mensajeOAuth }}
    </div>
    <div v-if="!status?.oauthConfigured" class="alert alert-warning py-2">
      El servidor no tiene <code>GMAIL_CLIENT_ID</code> y <code>GMAIL_CLIENT_SECRET</code>.
      Crea un cliente OAuth en
      <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener">Google Cloud</a>,
      activa Gmail API y pon esta Redirect URI:
      <code class="user-select-all">{{ oauthConfig?.redirectUri || 'https://vida.vicenteserran.com/api/gmail/callback' }}</code>
    </div>
    <div v-if="status?.needsReauth && !status?.connected" class="alert alert-warning py-2">
      La autorización de Gmail ha caducado o se ha revocado.
      Pulsa <strong>Conectar Gmail</strong> para volver a autorizar la cuenta.
    </div>
    <div v-if="error" class="alert alert-danger alert-dismissible py-2">
      {{ error }}
      <button type="button" class="btn-close" @click="error = ''"></button>
    </div>
    <div v-if="mensaje" class="alert alert-success alert-dismissible py-2">
      {{ mensaje }}
      <button type="button" class="btn-close" @click="mensaje = ''"></button>
    </div>

    <Teleport to="body">
      <div
        v-if="focusActivo && detalle"
        class="gmail-focus"
        role="dialog"
        aria-modal="true"
        aria-label="Modo focus"
      >
        <div class="gmail-focus-bar">
          <div class="d-flex gap-2 align-items-center">
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="!focusAnterior" title="Anterior (←)" @click="focusIr(focusAnterior)">
              <i class="bi bi-chevron-left"></i>
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" :disabled="!focusSiguiente" title="Siguiente (→)" @click="focusIr(focusSiguiente)">
              <i class="bi bi-chevron-right"></i>
            </button>
            <span class="small text-muted">{{ focusPosicion }}</span>
          </div>
          <div class="d-flex gap-2 flex-wrap">
            <button class="btn btn-success btn-sm" title="Archivar (E)" @click="accion('archivar')">Archivar</button>
            <button class="btn btn-outline-primary btn-sm" :disabled="accionando" @click="accion('leer')">Marcar leído</button>
            <button class="btn btn-outline-warning btn-sm" :disabled="accionando" @click="accion('estrella')">Destacar</button>
            <button class="btn btn-outline-danger btn-sm" :disabled="accionando" @click="accion('papelera')">Papelera</button>
            <button class="btn btn-dark btn-sm" title="Salir del modo focus (Esc o Mayús+F)" @click="focusActivo = false">
              <i class="bi bi-fullscreen-exit me-1"></i>Salir
            </button>
          </div>
        </div>
        <div class="gmail-focus-scroll">
          <article class="gmail-focus-article">
            <h1 class="h4 mb-1">{{ detalle.subject }}</h1>
            <p class="small text-muted mb-3">
              <strong>{{ detalle.fromName || detalle.fromEmail }}</strong>
              <span> &lt;{{ detalle.fromEmail }}&gt;</span>
              <span v-if="fechaDetalle"> · {{ fechaDetalle }}</span>
              <span v-if="cuerpoCargando"> · cargando…</span>
            </p>
            <iframe
              v-if="detalle.bodyHtml"
              :key="'focus-' + detalle.id"
              class="gmail-focus-frame"
              title="Cuerpo del correo (focus)"
              sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
              referrerpolicy="no-referrer"
              :srcdoc="detalle.bodyHtml"
              @load="ajustarFrameFocus"
            ></iframe>
            <pre v-else class="gmail-focus-text">{{ detalle.bodyText || detalle.snippet }}</pre>
          </article>
        </div>
      </div>
    </Teleport>

    <template v-if="status?.connected">
      <div class="gmail-stats mb-2" :class="{ 'gmail-stats-zero': bandejaTotal === 0 }">
        <button type="button" class="gmail-stat" :class="{ active: filtro === 'todos' }" @click="filtro = 'todos'">
          <strong>{{ bandejaTotal }}</strong> bandeja
          <span class="gmail-stat-sub">{{ bandejaUnread }} no leídos</span>
        </button>
        <button type="button" class="gmail-stat" :class="{ active: filtro === 'auto' }" @click="filtro = 'auto'">
          <strong class="text-success">{{ counts.auto }}</strong> seguras
          <span class="gmail-stat-sub">{{ counts.archivar }} archivar</span>
        </button>
        <button type="button" class="gmail-stat" :class="{ active: filtro === 'responder' }" @click="filtro = 'responder'">
          <strong class="text-primary">{{ counts.responder }}</strong> responder
          <span class="gmail-stat-sub">{{ counts.seguir }} seguir</span>
        </button>
        <button type="button" class="gmail-stat" :class="{ active: filtro === 'leer' }" @click="filtro = 'leer'">
          <strong>{{ counts.leer }}</strong> revisar
          <span class="gmail-stat-sub">{{ counts.papelera }} papelera</span>
        </button>
      </div>

      <div v-if="bandejaTotal === 0 && !cargando && !reponiendo" class="alert alert-success py-2">
        <i class="bi bi-check2-circle me-2"></i>Inbox Zero. La bandeja está vacía.
      </div>

      <ul class="nav nav-tabs mb-2 gmail-tabs" role="tablist">
        <li class="nav-item">
          <button class="nav-link py-1" :class="{ active: tab === 'bandeja' }" @click="tab = 'bandeja'">
            <i class="bi bi-inbox me-1"></i>Bandeja
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link py-1" :class="{ active: tab === 'agente' }" @click="tab = 'agente'; cargarChat()">
            <i class="bi bi-robot me-1"></i>Agente
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link py-1" :class="{ active: tab === 'reglas' }" @click="tab = 'reglas'; cargarReglas()">
            <i class="bi bi-funnel me-1"></i>Reglas
          </button>
        </li>
        <li class="nav-item">
          <button class="nav-link py-1" :class="{ active: tab === 'push' }" @click="tab = 'push'; cargarPushSetup()">
            <i class="bi bi-broadcast me-1"></i>Push
            <span
              v-if="pushSetup"
              class="badge ms-1"
              :class="pushSetup.watching ? 'bg-success' : 'bg-warning text-dark'"
            >{{ pushSetup.watching ? 'on' : 'off' }}</span>
          </button>
        </li>
      </ul>

      <div v-show="tab === 'bandeja'">
        <div class="d-flex flex-wrap gap-1 mb-2">
          <button
            v-for="f in filtros"
            :key="f.value"
            type="button"
            class="btn btn-sm py-0"
            :class="filtro === f.value ? 'btn-dark' : 'btn-outline-secondary'"
            @click="filtro = f.value"
          >
            {{ f.label }}
            <span class="badge bg-light text-dark ms-1">{{ f.count }}</span>
          </button>
        </div>
        <p v-if="quedanPorCargar && !cargando" class="small text-muted mb-2">
          Mostrando {{ messages.length }} de {{ bandejaTotal }} en la bandeja.
          <button
            v-if="nextPageToken"
            type="button"
            class="btn btn-link btn-sm p-0 align-baseline"
            :disabled="cargandoMas"
            @click="cargarSiguientes"
          >
            Cargar siguientes
          </button>
        </p>

        <div v-if="cargando && !messages.length" class="text-center py-5">
          <div class="spinner-border text-primary"></div>
        </div>
        <div v-else ref="workspaceEl" class="gmail-workspace" :class="{ 'gmail-arrastrando': arrastrandoAncho }">
          <div class="gmail-list-pane" :style="estiloLista">
            <TransitionGroup name="gmail-row" tag="div" class="list-group shadow-sm gmail-list-scroll">
              <div
                v-for="m in filtrados"
                :key="m.id"
                class="list-group-item list-group-item-action gmail-row"
                :class="{ active: seleccionado?.id === m.id, 'gmail-row-unread': m.unread }"
                role="button"
                tabindex="0"
                @click="abrir(m)"
                @keydown.enter.prevent="abrir(m)"
              >
                <div class="d-flex justify-content-between gap-2 align-items-start">
                  <span class="text-truncate gmail-row-from">{{ m.fromName || m.fromEmail || m.from }}</span>
                  <span class="d-flex align-items-center gap-1 flex-shrink-0">
                    <span v-if="fechaLista(m)" class="gmail-row-date" :title="fechaListaTitulo(m)">{{ fechaLista(m) }}</span>
                    <span class="badge" :class="badgeClase(m.clasificacion?.accion)">{{ m.clasificacion?.accion }}</span>
                  </span>
                </div>
                <div class="small text-truncate gmail-row-subject">{{ m.subject }}</div>
                <div class="d-flex justify-content-between gap-2 align-items-center">
                  <div class="small text-truncate gmail-row-snippet">{{ m.snippet }}</div>
                  <div class="d-flex gap-1 flex-shrink-0">
                    <button
                      type="button"
                      class="btn btn-sm btn-success py-0 px-2"
                      title="Archivar"
                      @click.stop="archivarEmail(m)"
                    >
                      Archivar
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-secondary py-0 px-2"
                      :disabled="resumiendoId === m.id || !status?.llmConfigured"
                      @click.stop="resumirEmail(m)"
                    >
                      <span v-if="resumiendoId === m.id" class="spinner-border spinner-border-sm"></span>
                      <template v-else>Resumir</template>
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="!filtrados.length" key="vacio" class="list-group-item text-muted small">
                No hay mensajes en este filtro.
              </div>
              <div v-if="nextPageToken" key="mas" class="list-group-item">
                <button
                  type="button"
                  class="btn btn-outline-primary btn-sm w-100"
                  :disabled="cargandoMas"
                  @click="cargarSiguientes"
                >
                  <span v-if="cargandoMas" class="spinner-border spinner-border-sm me-1"></span>
                  Cargar siguientes{{ pendientesSiguientes ? ` (${pendientesSiguientes})` : '' }}
                </button>
              </div>
            </TransitionGroup>
          </div>
          <div
            class="gmail-splitter"
            role="separator"
            aria-orientation="vertical"
            aria-label="Ancho de la lista de correos"
            title="Arrastra para cambiar el ancho de la lista"
            tabindex="0"
            @pointerdown="iniciarArrastreAncho"
            @pointermove="moverArrastreAncho"
            @pointerup="soltarArrastreAncho"
            @pointercancel="soltarArrastreAncho"
            @dblclick="resetAnchoLista"
            @keydown.left.prevent="ajustarAnchoLista(-24)"
            @keydown.right.prevent="ajustarAnchoLista(24)"
          ></div>
          <div class="gmail-reader-pane">
            <div v-if="detalle" class="gmail-reader-layout" :class="{ 'gmail-ia-open': panelIA }">
            <div class="card border-0 shadow-sm gmail-reader">
              <div class="card-body py-3">
                <div class="d-flex justify-content-between gap-2 flex-wrap mb-1">
                  <h3 class="h6 mb-0">{{ detalle.subject }}</h3>
                  <span class="badge" :class="badgeClase(detalle.clasificacion?.accion)">
                    {{ detalle.clasificacion?.accion }} · {{ detalle.clasificacion?.confianza }}
                  </span>
                </div>
                <p class="small mb-1">
                  <strong>{{ detalle.fromName || detalle.fromEmail }}</strong>
                  <span class="text-muted"> &lt;{{ detalle.fromEmail }}&gt;</span>
                  <span v-if="fechaDetalle" class="text-muted"> · {{ fechaDetalle }}</span>
                </p>
                <p class="small mb-2 text-muted">
                  {{ detalle.clasificacion?.motivo }}
                  <span v-if="cuerpoCargando"> · cargando…</span>
                </p>
                <div class="d-flex flex-wrap gap-2 mb-2">
                  <button
                    class="btn btn-outline-dark btn-sm"
                    :disabled="resumiendoId === detalle.id || !status?.llmConfigured"
                    @click="resumirEmail(detalle)"
                  >
                    <span v-if="resumiendoId === detalle.id" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="bi bi-stars me-1"></i>
                    Resumir
                  </button>
                  <button
                    class="btn btn-outline-info btn-sm"
                    :disabled="guardandoRaindrop || !status?.raindropConfigured"
                    :title="status?.raindropConfigured
                      ? 'Guardar como documento en Raindrop (visor, sin Gmail)'
                      : 'Configura Raindrop en Perfil para usar este botón'"
                    @click="guardarEnRaindrop"
                  >
                    <span v-if="guardandoRaindrop" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="bi bi-bookmark-plus me-1"></i>
                    Raindrop
                  </button>
                  <button class="btn btn-success btn-sm" title="Archivar (atajo: Shift+A o E)" @click="accion('archivar')">Archivar</button>
                  <button class="btn btn-outline-primary btn-sm" :disabled="accionando" @click="accion('leer')">Marcar leído</button>
                  <button class="btn btn-outline-warning btn-sm" :disabled="accionando" @click="accion('estrella')">Destacar</button>
                  <button class="btn btn-outline-danger btn-sm" :disabled="accionando" @click="accion('papelera')">Papelera</button>
                  <button
                    type="button"
                    class="btn btn-outline-dark btn-sm d-none d-lg-inline-block ms-auto"
                    title="Modo focus: leer el correo a pantalla completa (Mayús+F)"
                    @click="alternarFocus"
                  >
                    <i class="bi bi-fullscreen me-1"></i>Focus
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm d-none d-lg-inline-block"
                    :class="panelIA ? 'btn-primary' : 'btn-outline-primary'"
                    :aria-pressed="panelIA"
                    title="Chat y borrador de respuesta con IA"
                    @click="togglePanelIA"
                  >
                    <i class="bi bi-robot me-1"></i>IA
                  </button>
                </div>
                <div v-if="resumenActual" class="gmail-summary mb-2">
                  <div class="small fw-semibold mb-1">Resumen IA</div>
                  <div class="small gmail-summary-text">{{ resumenActual }}</div>
                </div>
                <div v-if="adjuntosVisibles.length" class="gmail-attachments mb-2">
                  <div class="small fw-semibold mb-1">
                    <i class="bi bi-paperclip me-1"></i>
                    {{ adjuntosVisibles.length }}
                    {{ adjuntosVisibles.length === 1 ? 'adjunto' : 'adjuntos' }}
                  </div>
                  <div class="d-flex flex-wrap gap-2">
                    <button
                      v-for="att in adjuntosVisibles"
                      :key="att.id"
                      type="button"
                      class="btn btn-outline-secondary btn-sm gmail-att-btn"
                      :disabled="descargandoId === att.id"
                      :title="esPdfAdjunto(att) ? `Ver ${att.filename}` : `Descargar ${att.filename}`"
                      @click="abrirAdjunto(att)"
                    >
                      <span v-if="descargandoId === att.id" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else :class="iconoAdjunto(att)" class="me-1"></i>
                      <span class="gmail-att-name">{{ att.filename }}</span>
                      <span v-if="att.size" class="text-muted ms-1">{{ tamanoAdjunto(att.size) }}</span>
                    </button>
                  </div>
                </div>
                <iframe
                  v-if="detalle.bodyHtml"
                  :key="detalle.id"
                  class="gmail-frame"
                  title="Cuerpo del correo"
                  sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                  referrerpolicy="no-referrer"
                  :srcdoc="detalle.bodyHtml"
                  @load="ajustarFrame"
                ></iframe>
                <pre v-else class="gmail-body small">{{ detalle.bodyText || detalle.snippet }}</pre>
              </div>
            </div>
            <aside class="card border-0 shadow-sm gmail-ia-panel" aria-label="Asistente IA del correo">
              <div class="card-body py-3">
                <div class="gmail-ia-head d-none d-lg-flex align-items-center justify-content-between mb-2">
                  <span class="fw-semibold"><i class="bi bi-robot me-1"></i>Asistente IA</span>
                  <button type="button" class="btn-close btn-sm" aria-label="Cerrar panel IA" @click="panelIA = false"></button>
                </div>
                <div class="gmail-mail-chat">
                  <div class="small fw-semibold mb-1">Chat sobre este correo</div>
                  <p v-if="!status?.llmConfigured" class="small text-muted mb-2">
                    Falta <code>DEEPSEEK_API_KEY</code> en el servidor.
                  </p>
                  <div v-if="chatCorreo.length" class="gmail-chat-mini mb-2">
                    <div v-for="(m, i) in chatCorreo" :key="i" class="mb-2">
                      <div class="small text-muted">{{ m.role === 'user' ? 'Tú' : 'Agente' }}</div>
                      <div class="gmail-bubble small" :class="m.role === 'user' ? 'gmail-bubble-user' : 'gmail-bubble-agent'">
                        {{ m.content }}
                      </div>
                    </div>
                  </div>
                  <form class="d-flex gap-2" @submit.prevent="preguntarCorreo">
                    <input
                      ref="preguntaCorreoEl"
                      v-model="preguntaCorreo"
                      class="form-control form-control-sm"
                      placeholder="Pregunta o pide un borrador, archivar, explicar un cobro…"
                      :disabled="preguntandoCorreo || !status?.llmConfigured"
                    >
                    <button class="btn btn-primary btn-sm" type="submit" :disabled="preguntandoCorreo || !preguntaCorreo.trim() || !status?.llmConfigured">
                      <span v-if="preguntandoCorreo" class="spinner-border spinner-border-sm"></span>
                      <i v-else class="bi bi-send"></i>
                    </button>
                  </form>
                </div>
                <div class="mt-3">
                  <label class="form-label small mb-1">Borrador de respuesta (no se envía)</label>
                  <textarea v-model="borrador" class="form-control form-control-sm gmail-borrador" rows="2" placeholder="Escribe el borrador…"></textarea>
                  <button class="btn btn-outline-secondary btn-sm mt-2" :disabled="accionando || !borrador.trim()" @click="crearBorrador">
                    Guardar borrador en Gmail
                  </button>
                </div>
              </div>
            </aside>
            </div>
            <p v-else class="text-muted small">Elige un correo para verlo, resumirlo o preguntar a la IA.</p>
          </div>
        </div>
      </div>

      <div v-show="tab === 'agente'">
        <div class="card border-0 shadow-sm">
          <div class="card-body">
            <p class="small text-muted mb-2">
              Agente DeepSeek sobre tu bandeja: resume, responde preguntas, archiva lo seguro o prepara borradores. No envía correos.
            </p>
            <div class="gmail-chat mb-3">
              <div v-for="m in chat" :key="m.id || m.temp" class="mb-2">
                <div class="small text-muted">{{ m.role === 'user' ? 'Tú' : 'Agente' }}</div>
                <div class="gmail-bubble" :class="m.role === 'user' ? 'gmail-bubble-user' : 'gmail-bubble-agent'">
                  {{ m.content }}
                </div>
              </div>
              <p v-if="!chat.length" class="text-muted small mb-0">
                Pregunta «¿cuántos hay?», «resume lo urgente» o «aplica las sugerencias seguras».
              </p>
            </div>
            <form class="d-flex gap-2" @submit.prevent="enviarAgente">
              <input v-model="pregunta" class="form-control" placeholder="Pregunta o pide algo sobre tus emails…" :disabled="preguntando">
              <button class="btn btn-primary" type="submit" :disabled="preguntando || !pregunta.trim()">
                <span v-if="preguntando" class="spinner-border spinner-border-sm"></span>
                <i v-else class="bi bi-send"></i>
              </button>
              <button type="button" class="btn btn-outline-secondary" :disabled="preguntando" @click="borrarChat">
                <i class="bi bi-trash"></i>
              </button>
            </form>
          </div>
        </div>
      </div>

      <div v-show="tab === 'reglas'">
        <p class="small text-muted">
          Las reglas ganan a las heurísticas. Útiles para «archiva siempre @linkedin.com» o «responde siempre a este remitente».
        </p>
        <form class="row g-2 align-items-end mb-3" @submit.prevent="guardarRegla">
          <div class="col-md-3">
            <label class="form-label small">Tipo</label>
            <select v-model="nuevaRegla.tipo" class="form-select form-select-sm">
              <option value="dominio">Dominio</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label small">Valor</label>
            <input v-model="nuevaRegla.valor" class="form-control form-control-sm" placeholder="news.com o jefe@x.com">
          </div>
          <div class="col-md-3">
            <label class="form-label small">Acción</label>
            <select v-model="nuevaRegla.accion" class="form-select form-select-sm">
              <option value="archivar">Archivar</option>
              <option value="papelera">Papelera</option>
              <option value="responder">Responder</option>
              <option value="seguir">Seguir</option>
              <option value="leer">Leer</option>
            </select>
          </div>
          <div class="col-md-2">
            <button class="btn btn-primary btn-sm w-100" type="submit">Añadir</button>
          </div>
        </form>
        <div class="table-responsive">
          <table class="table table-sm">
            <thead>
              <tr><th>Tipo</th><th>Valor</th><th>Acción</th><th></th></tr>
            </thead>
            <tbody>
              <tr v-for="r in reglas" :key="r.id">
                <td>{{ r.tipo }}</td>
                <td><code>{{ r.valor }}</code></td>
                <td>{{ r.accion }}</td>
                <td class="text-end">
                  <button class="btn btn-outline-danger btn-sm" @click="borrarRegla(r.id)">Quitar</button>
                </td>
              </tr>
              <tr v-if="!reglas.length">
                <td colspan="4" class="text-muted small">Sin reglas todavía.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-show="tab === 'push'" class="card border-0 shadow-sm">
        <div class="card-body">
          <h3 class="h6 mb-2">Webhook Gmail (Pub/Sub)</h3>
          <p class="small text-muted mb-3">
            Para seguimiento y otras automatizaciones Gmail avisa al servidor en el momento,
            sin esperar al cron de 30 min. Hay que crear el topic una vez en Google Cloud
            (el mismo proyecto del OAuth) y luego activar el watch aquí.
          </p>
          <p v-if="pushSetup?.watching" class="alert alert-success py-2 small">
            Webhook activo{{ pushSetup.watchExpiration ? ` hasta ${fechaWatch}` : '' }}.
          </p>
          <ol class="small mb-3 ps-3">
            <li class="mb-2">
              Activa
              <a :href="pushSetup?.consoleApi" target="_blank" rel="noopener">Cloud Pub/Sub API</a>.
            </li>
            <li class="mb-2">
              Crea el topic
              <code class="user-select-all">{{ pushSetup?.topicId || 'gastos-gmail' }}</code>
              en
              <a :href="pushSetup?.consoleTopics" target="_blank" rel="noopener">Pub/Sub → Topics</a>
              (proyecto <code>{{ pushSetup?.projectNumber || '…' }}</code>).
              <button type="button" class="btn btn-link btn-sm p-0 ms-1" @click="copiar(pushSetup?.topicName)">Copiar nombre</button>
            </li>
            <li class="mb-2">
              En el topic, permisos: añade
              <code class="user-select-all">{{ pushSetup?.publisher }}</code>
              con rol <strong>Pub/Sub Publisher</strong>.
              <button type="button" class="btn btn-link btn-sm p-0 ms-1" @click="copiar(pushSetup?.publisher)">Copiar</button>
            </li>
            <li class="mb-2">
              Crea una suscripción <strong>Push</strong> llamada
              <code>{{ pushSetup?.subscriptionId }}</code>
              hacia:
              <div class="input-group input-group-sm mt-1">
                <input class="form-control" readonly :value="pushSetup?.pushUrlWithToken || pushSetup?.pushUrl">
                <button type="button" class="btn btn-outline-secondary" @click="copiar(pushSetup?.pushUrlWithToken || pushSetup?.pushUrl)">Copiar URL</button>
              </div>
            </li>
            <li>Pulsa <strong>Activar watch</strong>.</li>
          </ol>
          <p class="small mb-2">O pega esto en Cloud Shell del mismo proyecto:</p>
          <pre class="small bg-light border rounded p-2 mb-2 user-select-all" style="white-space: pre-wrap;">{{ pushSetup?.gcloudCommand }}</pre>
          <button type="button" class="btn btn-outline-secondary btn-sm me-2" @click="copiar(pushSetup?.gcloudCommand)">Copiar comandos</button>
          <button type="button" class="btn btn-primary btn-sm" :disabled="activandoWatch" @click="activarWatch">
            <span v-if="activandoWatch" class="spinner-border spinner-border-sm me-1"></span>
            Activar watch
          </button>
        </div>
      </div>
    </template>
    <div v-if="avisoAccion" class="gmail-undo" role="status">
      <span>{{ avisoAccion.texto }}</span>
      <button type="button" @click="deshacerAccion">Deshacer</button>
    </div>
    <Teleport to="body">
      <div
        v-if="visorPdf || visorCargando"
        class="gmail-pdf-overlay"
        role="dialog"
        aria-modal="true"
        :aria-label="visorPdf?.filename || 'Visor PDF'"
        @click.self="cerrarVisorPdf"
      >
        <div class="gmail-pdf-dialog">
          <div class="gmail-pdf-bar">
            <div class="gmail-pdf-title">
              <i class="bi bi-file-earmark-pdf me-2"></i>
              <span class="text-truncate">{{ visorPdf?.filename || 'Cargando PDF…' }}</span>
            </div>
            <div class="d-flex gap-2 flex-shrink-0">
              <button
                type="button"
                class="btn btn-sm btn-outline-light"
                :disabled="!visorPdf"
                @click="descargarDesdeVisor"
              >
                <i class="bi bi-download me-1"></i>Descargar
              </button>
              <button type="button" class="btn btn-sm btn-light" @click="cerrarVisorPdf">Cerrar</button>
            </div>
          </div>
          <div class="gmail-pdf-body">
            <div v-if="visorCargando && !visorPdf" class="gmail-pdf-loading">
              <div class="spinner-border text-light"></div>
              <span class="small mt-2">Cargando PDF…</span>
            </div>
            <iframe
              v-else-if="visorPdf"
              class="gmail-pdf-frame"
              title="Visor PDF"
              :src="visorPdf.url"
            ></iframe>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import axios from '../config/axios';
import { aplicarConteoBandeja, gmailInboxTick, setGmailInboxCount } from '../utils/navBadges';
import {
  ajustarCountsAlQuitar,
  ajustarCountsAlRestaurar,
  anchoListaBandeja,
  esNoLeido,
  esNoLeidoReciente,
  fechaListaCorta,
  fechaMensajeMs,
  insertarEnIndice,
  recontarMensajes,
  siguienteVisible,
} from '../utils/gmailBandeja';

export default {
  name: 'GmailInbox',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const status = ref(null);
    const oauthConfig = ref(null);
    const conectando = ref(false);
    const cargando = ref(false);
    const cambiandoPausa = ref(false);
    const mostrarHorario = ref(false);
    const guardandoHorario = ref(false);
    const diasSemana = [
      { idx: 1, nombre: 'Lunes' },
      { idx: 2, nombre: 'Martes' },
      { idx: 3, nombre: 'Miércoles' },
      { idx: 4, nombre: 'Jueves' },
      { idx: 5, nombre: 'Viernes' },
      { idx: 6, nombre: 'Sábado' },
      { idx: 0, nombre: 'Domingo' },
    ];
    const horarioForm = ref(nuevoHorarioForm());
    const excepciones = ref([]);
    const nuevaExcepcion = ref('');
    const guardandoExcepcion = ref(false);
    const aplicando = ref(false);
    const accionando = ref(false);
    const guardandoRaindrop = ref(false);
    const cuerpoCargando = ref(false);
    const avisoAccion = ref(null);
    const reponiendo = ref(false);
    const error = ref('');
    const mensaje = ref('');
    const mensajeOAuth = ref('');
    const mensajeOAuthOk = ref(true);
    const messages = ref([]);
    const counts = ref({ total: 0, unread: 0, archivar: 0, responder: 0, seguir: 0, leer: 0, papelera: 0, tarea: 0, auto: 0 });
    const seleccionado = ref(null);
    const detalle = ref(null);
    const tab = ref('bandeja');
    const filtro = ref('todos');
    const chat = ref([]);
    const pregunta = ref('');
    const preguntando = ref(false);
    const reglas = ref([]);
    const nuevaRegla = ref({ tipo: 'dominio', valor: '', accion: 'archivar' });
    const pushSetup = ref(null);
    const activandoWatch = ref(false);
    const borrador = ref('');
    const resumenes = ref({});
    const chatsCorreo = ref({});
    const resumiendoId = ref(null);
    const operaciones = new Map();
    let abrirSeq = 0;
    let avisoTimer = null;
    const preguntaCorreo = ref('');
    const preguntandoCorreo = ref(false);
    const descargandoId = ref(null);
    const visorPdf = ref(null);
    const visorCargando = ref(false);
    const inboxTotal = ref(0);
    const inboxUnread = ref(0);
    const nextPageToken = ref(null);
    const cargandoMas = ref(false);
    const INBOX_PAGE_MAX = 500;
    // Página de la lista: pocas filas = primera carga rápida (como Gmail); el resto bajo demanda.
    const INBOX_LIST_PAGE = 50;
    const ANCHO_LISTA_KEY = 'gmail-inbox-list-width';
    const workspaceEl = ref(null);
    const arrastrandoAncho = ref(false);
    const anchoLista = ref(leerAnchoLista());
    const PANEL_IA_KEY = 'gmail-inbox-panel-ia';
    const panelIA = ref(leerPanelIA());
    const preguntaCorreoEl = ref(null);
    const focusActivo = ref(false);

    function leerPanelIA() {
      try {
        return localStorage.getItem(PANEL_IA_KEY) === '1';
      } catch {
        return false;
      }
    }

    watch(panelIA, (abierto) => {
      try {
        localStorage.setItem(PANEL_IA_KEY, abierto ? '1' : '0');
      } catch {
        /* sin almacenamiento: solo esta sesión */
      }
    });

    function togglePanelIA() {
      panelIA.value = !panelIA.value;
      if (panelIA.value) nextTick(() => preguntaCorreoEl.value?.focus());
    }
    let anchoPreferido = anchoLista.value;
    let arrastreInicioX = 0;
    let arrastreInicioAncho = 0;

    function leerAnchoLista() {
      try {
        return anchoListaBandeja(localStorage.getItem(ANCHO_LISTA_KEY));
      } catch {
        return anchoListaBandeja(null);
      }
    }

    function guardarAnchoLista(px) {
      try {
        localStorage.setItem(ANCHO_LISTA_KEY, String(px));
      } catch {
        /* el ancho sigue valiendo en esta sesión */
      }
    }

    const estiloLista = computed(() => ({ width: `${anchoLista.value}px` }));

    function aplicarAnchoLista(px, { persist = false } = {}) {
      const preferido = anchoListaBandeja(px);
      anchoPreferido = preferido;
      const container = workspaceEl.value?.clientWidth || 0;
      anchoLista.value = anchoListaBandeja(preferido, { container });
      if (persist) guardarAnchoLista(preferido);
    }

    function encajarAnchoLista() {
      if (!workspaceEl.value) return;
      anchoLista.value = anchoListaBandeja(anchoPreferido, { container: workspaceEl.value.clientWidth });
    }

    function iniciarArrastreAncho(ev) {
      if (ev.pointerType === 'mouse' && ev.button !== 0) return;
      arrastrandoAncho.value = true;
      arrastreInicioX = ev.clientX;
      arrastreInicioAncho = anchoPreferido;
      ev.currentTarget.setPointerCapture?.(ev.pointerId);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    function moverArrastreAncho(ev) {
      if (!arrastrandoAncho.value) return;
      aplicarAnchoLista(arrastreInicioAncho + (ev.clientX - arrastreInicioX));
    }

    function soltarArrastreAncho(ev) {
      if (!arrastrandoAncho.value) return;
      arrastrandoAncho.value = false;
      try {
        ev.currentTarget.releasePointerCapture?.(ev.pointerId);
      } catch {
        /* el puntero ya no está capturado */
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      guardarAnchoLista(anchoPreferido);
    }

    function ajustarAnchoLista(delta) {
      aplicarAnchoLista(anchoPreferido + delta, { persist: true });
    }

    function resetAnchoLista() {
      aplicarAnchoLista(420, { persist: true });
    }

    function fechaLista(message) {
      return fechaListaCorta(message);
    }

    function fechaListaTitulo(message) {
      const ms = fechaMensajeMs(message);
      if (!Number.isFinite(ms)) return '';
      return new Intl.DateTimeFormat('es-ES', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(ms));
    }

    const filtrados = computed(() => {
      if (filtro.value === 'todos') return messages.value;
      if (filtro.value === 'sinleer') return messages.value.filter((m) => esNoLeidoReciente(m));
      if (filtro.value === 'auto') return messages.value.filter((m) => m.clasificacion?.auto);
      return messages.value.filter((m) => m.clasificacion?.accion === filtro.value);
    });

    const filtros = computed(() => [
      { value: 'todos', label: 'Todos', count: bandejaTotal.value },
      { value: 'sinleer', label: 'Sin leer (ayer y hoy)', count: messages.value.filter((m) => esNoLeidoReciente(m)).length },
      { value: 'auto', label: 'Seguras', count: counts.value.auto },
      { value: 'archivar', label: 'Archivar', count: counts.value.archivar },
      { value: 'responder', label: 'Responder', count: counts.value.responder },
      { value: 'seguir', label: 'Seguir', count: counts.value.seguir },
      { value: 'leer', label: 'Leer', count: counts.value.leer },
    ]);

    const bandejaTotal = computed(() => Math.max(inboxTotal.value, counts.value.total, messages.value.length));
    const bandejaUnread = computed(() => (inboxTotal.value > 0 ? inboxUnread.value : counts.value.unread));
    const quedanPorCargar = computed(() => !!nextPageToken.value || bandejaTotal.value > messages.value.length);
    const pendientesSiguientes = computed(() => Math.max(0, bandejaTotal.value - messages.value.length));

    function aplicarTotalesGmail({ inboxTotal: total, inboxUnread: unread } = {}) {
      if (total != null && Number.isFinite(Number(total))) {
        inboxTotal.value = Math.max(0, Math.floor(Number(total)));
        aplicarConteoBandeja({ inboxTotal: inboxTotal.value });
      }
      if (unread != null && Number.isFinite(Number(unread))) {
        inboxUnread.value = Math.max(0, Math.floor(Number(unread)));
      }
    }

    function aplicarPaginaBandeja(data, { append = false } = {}) {
      const incoming = data.messages || [];
      if (append) {
        const seen = new Set(messages.value.map((m) => m.id));
        messages.value = messages.value.concat(incoming.filter((m) => !seen.has(m.id)));
      } else {
        messages.value = incoming;
      }
      counts.value = recontarMensajes(messages.value);
      nextPageToken.value = data.nextPageToken || null;
      aplicarTotalesGmail({
        inboxTotal: data.inboxTotal,
        inboxUnread: data.inboxUnread,
      });
    }

    const etiquetaAgente = computed(() => {
      if (status.value?.agentMode === 'deepseek') {
        return `DeepSeek${status.value.llmModel ? ` (${status.value.llmModel})` : ''}`;
      }
      if (status.value?.agentMode === 'openai') return 'IA';
      return 'reglas';
    });
    const etiquetaPush = computed(() => status.value?.push?.label || 'en vivo');
    const fechaWatch = computed(() => {
      const raw = Number(pushSetup.value?.watchExpiration);
      if (!Number.isFinite(raw) || raw <= 0) return '';
      return new Date(raw).toLocaleString('es-ES');
    });

    const resumenActual = computed(() => {
      const id = detalle.value?.id;
      return id ? resumenes.value[id] || '' : '';
    });

    const chatCorreo = computed(() => {
      const id = detalle.value?.id;
      return id ? chatsCorreo.value[id] || [] : [];
    });

    const adjuntosVisibles = computed(() => {
      const list = detalle.value?.attachments || [];
      return list.filter((att) => {
        if (!att?.id) return false;
        const mime = String(att.mimeType || '').toLowerCase();
        const tinyPixel = att.inline && mime.startsWith('image/') && att.size > 0 && att.size < 250;
        return !tinyPixel;
      });
    });

    function badgeClase(accion) {
      if (accion === 'archivar') return 'bg-success';
      if (accion === 'responder') return 'bg-primary';
      if (accion === 'seguir') return 'bg-info text-dark';
      if (accion === 'papelera') return 'bg-danger';
      return 'bg-secondary';
    }

    const fechaDetalle = computed(() => {
      const d = detalle.value;
      if (!d) return '';
      const ms = d.internalDate ? Number(d.internalDate) : Date.parse(d.date || '');
      if (!Number.isFinite(ms)) return d.date || '';
      return new Intl.DateTimeFormat('es-ES', {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(ms));
    });

    function iconoAdjunto(att) {
      const m = String(att?.mimeType || '').toLowerCase();
      const name = String(att?.filename || '').toLowerCase();
      if (m.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/.test(name)) return 'bi bi-file-image';
      if (m.includes('pdf') || name.endsWith('.pdf')) return 'bi bi-file-earmark-pdf';
      if (m.includes('zip') || m.includes('compressed') || /\.(zip|rar|7z)$/.test(name)) return 'bi bi-file-earmark-zip';
      if (m.includes('spreadsheet') || m.includes('excel') || /\.(xlsx?|csv)$/.test(name)) return 'bi bi-file-earmark-excel';
      if (m.includes('word') || /\.(docx?)$/.test(name)) return 'bi bi-file-earmark-word';
      if (m.includes('calendar') || name.endsWith('.ics')) return 'bi bi-calendar-event';
      return 'bi bi-paperclip';
    }

    function tamanoAdjunto(n) {
      const b = Number(n) || 0;
      if (b < 1024) return `${b} B`;
      if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
      return `${(b / (1024 * 1024)).toFixed(1)} MB`;
    }

    function esPdfAdjunto(att, blob) {
      const mime = String(att?.mimeType || blob?.type || '').toLowerCase();
      const name = String(att?.filename || '').toLowerCase();
      return mime.includes('pdf') || name.endsWith('.pdf');
    }

    function errorDesdeBlob(e, fallback) {
      const data = e.response?.data;
      if (typeof Blob !== 'undefined' && data instanceof Blob) {
        return data.text().then((text) => {
          try {
            const json = JSON.parse(text);
            return json?.error || fallback;
          } catch {
            return fallback;
          }
        });
      }
      if (data?.error) return Promise.resolve(data.error);
      if (e.message && !e.response) return Promise.resolve(e.message);
      return Promise.resolve(fallback);
    }

    async function obtenerBlobAdjunto(att) {
      const res = await axios.get(
        `/api/gmail/mensajes/${detalle.value.id}/adjunto`,
        {
          params: { part: att.id },
          responseType: 'blob',
          timeout: 120000,
          headers: { Accept: 'application/octet-stream, application/pdf, */*' },
        }
      );
      const blob = res.data;
      if (blob && blob.type && blob.type.includes('application/json')) {
        const text = await blob.text();
        let msg = 'No se pudo abrir el adjunto.';
        try { msg = JSON.parse(text).error || msg; } catch { /* blob no JSON */ }
        throw new Error(msg);
      }
      return blob;
    }

    function blobParaPdf(blob) {
      if (blob && /pdf/i.test(blob.type || '')) return blob;
      return new Blob([blob], { type: 'application/pdf' });
    }

    function cerrarVisorPdf() {
      if (visorPdf.value?.url) URL.revokeObjectURL(visorPdf.value.url);
      visorPdf.value = null;
      visorCargando.value = false;
      document.body.style.overflow = '';
    }

    function descargarBlob(blob, filename) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'adjunto';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }

    async function abrirVisorPdf(att) {
      if (!detalle.value?.id || !att?.id || descargandoId.value) return;
      descargandoId.value = att.id;
      visorCargando.value = true;
      document.body.style.overflow = 'hidden';
      error.value = '';
      if (visorPdf.value?.url) URL.revokeObjectURL(visorPdf.value.url);
      visorPdf.value = null;
      try {
        const raw = await obtenerBlobAdjunto(att);
        const blob = blobParaPdf(raw);
        visorPdf.value = {
          filename: att.filename || 'documento.pdf',
          url: URL.createObjectURL(blob),
          blob,
        };
      } catch (e) {
        document.body.style.overflow = '';
        visorCargando.value = false;
        error.value = await errorDesdeBlob(e, e.message || 'No se pudo abrir el PDF.');
      } finally {
        descargandoId.value = null;
        visorCargando.value = false;
      }
    }

    function descargarDesdeVisor() {
      if (!visorPdf.value) return;
      descargarBlob(visorPdf.value.blob, visorPdf.value.filename);
    }

    async function descargarAdjunto(att) {
      if (!detalle.value?.id || !att?.id || descargandoId.value) return;
      descargandoId.value = att.id;
      error.value = '';
      try {
        const blob = await obtenerBlobAdjunto(att);
        descargarBlob(blob, att.filename || 'adjunto');
      } catch (e) {
        error.value = await errorDesdeBlob(e, 'No se pudo descargar el adjunto.');
      } finally {
        descargandoId.value = null;
      }
    }

    async function abrirAdjunto(att) {
      if (esPdfAdjunto(att)) await abrirVisorPdf(att);
      else await descargarAdjunto(att);
    }

    // El cuerpo del correo vive en un iframe: sus teclas no llegan al window,
    // así que se reenvían al manejador de atajos de la bandeja.
    function enlazarTeclasFrame(doc) {
      if (!doc || doc.__atajosBandeja) return;
      doc.__atajosBandeja = true;
      doc.addEventListener('keydown', onTeclaBandeja);
    }

    function ajustarFrame(ev) {
      const frame = ev.target;
      try {
        const doc = frame.contentDocument;
        enlazarTeclasFrame(doc);
        const h = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight || 360;
        const max = Math.max(window.innerHeight - 220, 420);
        frame.style.height = `${Math.min(Math.max(h + 8, 280), max)}px`;
      } catch {
        frame.style.height = '480px';
      }
    }

    function ajustarFrameFocus(ev) {
      const frame = ev.target;
      try {
        const doc = frame.contentDocument;
        enlazarTeclasFrame(doc);
        const h = doc?.documentElement?.scrollHeight || doc?.body?.scrollHeight || 360;
        frame.style.height = `${Math.max(h + 8, 280)}px`;
      } catch {
        frame.style.height = '80vh';
      }
    }

    const focusIndice = computed(() => filtrados.value.findIndex((m) => m.id === detalle.value?.id));
    const focusAnterior = computed(() => filtrados.value[focusIndice.value - 1] || null);
    const focusSiguiente = computed(() => (focusIndice.value >= 0 ? filtrados.value[focusIndice.value + 1] || null : null));
    const focusPosicion = computed(() => (focusIndice.value >= 0 ? `${focusIndice.value + 1} / ${filtrados.value.length}` : ''));

    function focusIr(m) {
      if (m) abrir(m);
    }

    function alternarFocus() {
      if (focusActivo.value) {
        focusActivo.value = false;
      } else if (detalle.value?.id && tab.value === 'bandeja') {
        focusActivo.value = true;
      }
    }

    watch(focusActivo, (on) => {
      document.body.style.overflow = on ? 'hidden' : '';
    });
    watch(detalle, (d) => {
      if (!d && focusActivo.value) focusActivo.value = false;
    });

    function leerQuery() {
      const q = route.query;
      if (q.gmail === 'ok') {
        mensajeOAuth.value = 'Gmail conectado correctamente.';
        mensajeOAuthOk.value = true;
      } else if (q.gmail === 'error') {
        mensajeOAuth.value = `Error al conectar Gmail: ${q.message || 'desconocido'}`;
        mensajeOAuthOk.value = false;
      }
      if (q.filtro === 'sinleer') {
        filtro.value = 'sinleer';
      }
      if (q.gmail || q.filtro) {
        router.replace({ path: '/gmail', query: {} });
      }
    }

    function marcarReauthGmail(payload = {}) {
      status.value = {
        ...(status.value || {}),
        connected: false,
        needsReauth: true,
        email: '',
        inboxTotal: null,
        inboxUnread: null,
        ...payload,
      };
      messages.value = [];
      detalle.value = null;
      seleccionado.value = null;
      inboxTotal.value = 0;
      inboxUnread.value = 0;
      nextPageToken.value = null;
      setGmailInboxCount(0);
    }

    function errorGmail(e, fallback) {
      const data = e?.response?.data;
      if (data?.needsReauth || data?.code === 'NEEDS_REAUTH') {
        marcarReauthGmail({
          oauthConfigured: status.value?.oauthConfigured ?? true,
          agentMode: status.value?.agentMode,
          llmConfigured: status.value?.llmConfigured,
          llmModel: status.value?.llmModel,
        });
        return data.error
          || 'La autorización de Gmail ha caducado o se ha revocado. Vuelve a conectar la cuenta.';
      }
      const msg = data?.error || e?.message || fallback;
      if (/token has been expired or revoked|expired or revoked|invalid_grant/i.test(String(msg))) {
        marcarReauthGmail({
          oauthConfigured: status.value?.oauthConfigured ?? true,
          agentMode: status.value?.agentMode,
          llmConfigured: status.value?.llmConfigured,
          llmModel: status.value?.llmModel,
        });
        return 'La autorización de Gmail ha caducado o se ha revocado. Vuelve a conectar la cuenta.';
      }
      return msg || fallback;
    }

    function nuevoHorarioForm(h) {
      const dias = {};
      for (let d = 0; d <= 6; d += 1) {
        dias[d] = (h?.dias?.[d] || []).map((t) => ({ desde: t.desde, hasta: t.hasta }));
      }
      return { enabled: !!h?.enabled, tz: h?.tz || 'Europe/Madrid', dias };
    }

    const horarioActivo = computed(() => !!status.value?.pausa?.horario?.enabled);

    async function cargarExcepciones() {
      try {
        const { data } = await axios.get('/api/gmail/pausa/excepciones');
        excepciones.value = data.excepciones || [];
      } catch (e) {
        error.value = errorGmail(e, 'No se pudieron cargar los remitentes');
      }
    }

    function abrirHorario() {
      if (!mostrarHorario.value) {
        horarioForm.value = nuevoHorarioForm(status.value?.pausa?.horario);
        cargarExcepciones();
      }
      mostrarHorario.value = !mostrarHorario.value;
    }

    async function anadirExcepcion() {
      if (guardandoExcepcion.value || !nuevaExcepcion.value.trim()) return;
      guardandoExcepcion.value = true;
      error.value = '';
      try {
        const { data } = await axios.post('/api/gmail/pausa/excepciones', { valor: nuevaExcepcion.value });
        excepciones.value = data.excepciones || [];
        nuevaExcepcion.value = '';
      } catch (e) {
        error.value = errorGmail(e, 'No se pudo añadir el remitente');
      } finally {
        guardandoExcepcion.value = false;
      }
    }

    async function quitarExcepcion(id) {
      error.value = '';
      try {
        const { data } = await axios.delete(`/api/gmail/pausa/excepciones/${id}`);
        excepciones.value = data.excepciones || [];
      } catch (e) {
        error.value = errorGmail(e, 'No se pudo quitar el remitente');
      }
    }

    function anadirTramo(idx) {
      horarioForm.value.dias[idx].push({ desde: '09:00', hasta: '13:00' });
    }

    function quitarTramo(idx, i) {
      horarioForm.value.dias[idx].splice(i, 1);
    }

    function copiarTramos(origen, destinos) {
      for (const d of destinos) {
        horarioForm.value.dias[d] = horarioForm.value.dias[origen].map((t) => ({ ...t }));
      }
    }

    async function guardarHorario() {
      if (guardandoHorario.value) return;
      guardandoHorario.value = true;
      error.value = '';
      mensaje.value = '';
      try {
        const { data } = await axios.put('/api/gmail/pausa/horario', horarioForm.value);
        status.value = { ...status.value, pausa: data };
        horarioForm.value = nuevoHorarioForm(data.horario);
        mensaje.value = data.horario.enabled
          ? 'Horario de pausa guardado y activo.'
          : 'Horario guardado (desactivado).';
        mostrarHorario.value = false;
        if (!data.activa) await cargarInbox();
      } catch (e) {
        error.value = errorGmail(e, 'No se pudo guardar el horario');
      } finally {
        guardandoHorario.value = false;
      }
    }

    const pausaActiva = computed(() => !!status.value?.pausa?.activa);
    const pausaDesde = computed(() => status.value?.pausa?.desde || null);
    const pausaHasta = computed(() => status.value?.pausa?.hasta || null);
    function formatearHora(iso) {
      if (!iso) return '';
      const tz = status.value?.pausa?.horario?.tz;
      const opts = { hour: '2-digit', minute: '2-digit', ...(tz ? { timeZone: tz } : {}) };
      return new Date(iso).toLocaleTimeString('es-ES', opts);
    }
    const horaPausa = computed(() => formatearHora(pausaDesde.value));
    const horaFinPausa = computed(() => formatearHora(pausaHasta.value));

    async function alternarPausa() {
      if (cambiandoPausa.value) return;
      cambiandoPausa.value = true;
      error.value = '';
      mensaje.value = '';
      const activar = !pausaActiva.value;
      try {
        const { data } = await axios.post('/api/gmail/pausa', { activa: activar });
        status.value = {
          ...status.value,
          pausa: {
            activa: data.activa,
            origen: data.origen,
            horario: data.horario,
            desde: data.desde,
            hasta: data.hasta,
            horarioEstado: data.horarioEstado,
            enCola: null,
          },
        };
        if (!activar) {
          mensaje.value = data.released
            ? `Bandeja reanudada: ${data.released} correo(s) de la cola ya están en la bandeja.`
            : 'Bandeja reanudada. No había correos en la cola.';
          await cargarInbox();
        } else {
          mensaje.value = 'Bandeja en pausa: los correos nuevos irán a la cola.';
        }
      } catch (e) {
        error.value = errorGmail(e, 'No se pudo cambiar la pausa');
      } finally {
        cambiandoPausa.value = false;
      }
    }

    async function cargarStatus() {
      const { data } = await axios.get('/api/gmail/status');
      status.value = data;
      if (data.connected) {
        aplicarTotalesGmail({ inboxTotal: data.inboxTotal, inboxUnread: data.inboxUnread });
      } else {
        inboxTotal.value = 0;
        inboxUnread.value = 0;
        setGmailInboxCount(0);
      }
    }

    async function cargarOauthConfig() {
      try {
        const { data } = await axios.get('/api/gmail/oauth-config');
        oauthConfig.value = data;
      } catch {
        oauthConfig.value = null;
      }
    }

    async function conectar() {
      conectando.value = true;
      error.value = '';
      try {
        const { data } = await axios.get('/api/gmail/oauth-url');
        window.location.href = data.url;
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo iniciar OAuth de Gmail.';
        conectando.value = false;
      }
    }

    async function desconectar() {
      if (!window.confirm('¿Desconectar Gmail de esta app?')) return;
      cerrarVisorPdf();
      await axios.delete('/api/gmail/config');
      status.value = { ...status.value, connected: false, needsReauth: false, email: '' };
      messages.value = [];
      detalle.value = null;
      inboxTotal.value = 0;
      inboxUnread.value = 0;
      nextPageToken.value = null;
      setGmailInboxCount(0);
    }

    let recargaPendiente = false;
    async function cargarInbox() {
      if (!status.value?.connected) return;
      // Dos cargas a la vez (p. ej. al reanudar la pausa + aviso en vivo) saturan el límite de Gmail.
      if (cargando.value) {
        recargaPendiente = true;
        return;
      }
      cargando.value = true;
      error.value = '';
      try {
        const { data } = await axios.get('/api/gmail/inbox', {
          params: { max: INBOX_LIST_PAGE },
          timeout: 90000,
        });
        const ocultos = new Set(
          [...operaciones.values()]
            .filter((op) => !op.undone && !op.restoredLocal)
            .map((op) => op.id)
        );
        aplicarPaginaBandeja({
          ...data,
          messages: (data.messages || []).filter((item) => !ocultos.has(item.id)),
        });
        if (seleccionado.value && !messages.value.some((m) => m.id === seleccionado.value.id)) {
          seleccionado.value = null;
          detalle.value = null;
        }
      } catch (e) {
        error.value = errorGmail(e, 'No se pudo leer la bandeja.');
      } finally {
        cargando.value = false;
        if (recargaPendiente) {
          recargaPendiente = false;
          cargarInbox();
        }
      }
    }

    async function cargarSiguientes() {
      if (!nextPageToken.value || cargandoMas.value) return;
      cargandoMas.value = true;
      error.value = '';
      try {
        const { data } = await axios.get('/api/gmail/inbox', {
          params: { max: INBOX_LIST_PAGE, pageToken: nextPageToken.value },
          timeout: 90000,
        });
        aplicarPaginaBandeja(data, { append: true });
      } catch (e) {
        error.value = errorGmail(e, 'No se pudieron cargar más correos.');
      } finally {
        cargandoMas.value = false;
      }
    }

    async function aplicarSeguras() {
      aplicando.value = true;
      error.value = '';
      try {
        const { data } = await axios.post('/api/gmail/aplicar-sugerencias', { max: INBOX_PAGE_MAX }, { timeout: 120000 });
        mensaje.value = data.message;
        await cargarInbox();
        await cargarStatus();
      } catch (e) {
        error.value = errorGmail(e, 'No se pudieron aplicar las sugerencias.');
      } finally {
        aplicando.value = false;
      }
    }

    async function abrir(m) {
      if (!m?.id) return;
      if (visorPdf.value || visorCargando.value) cerrarVisorPdf();
      const seq = ++abrirSeq;
      seleccionado.value = m;
      borrador.value = '';
      preguntaCorreo.value = '';
      if (detalle.value?.id !== m.id || !detalle.value?.bodyHtml) {
        detalle.value = {
          ...m,
          bodyHtml: '',
          bodyText: m.bodyText || m.snippet || '',
        };
      }
      cuerpoCargando.value = true;
      try {
        const { data } = await axios.get(`/api/gmail/mensajes/${m.id}`, { timeout: 30000 });
        if (seq !== abrirSeq) return;
        detalle.value = data;
      } catch (e) {
        if (seq !== abrirSeq) return;
        error.value = e.response?.data?.error || 'No se pudo abrir el mensaje.';
        detalle.value = {
          ...m,
          bodyHtml: '',
          bodyText: m.bodyText || m.snippet || '',
        };
      } finally {
        if (seq === abrirSeq) cuerpoCargando.value = false;
      }
    }

    async function resumirEmail(m) {
      if (!m?.id) return;
      tab.value = 'bandeja';
      if (seleccionado.value?.id !== m.id) {
        await abrir(m);
      }
      if (resumenes.value[m.id]) return;
      resumiendoId.value = m.id;
      error.value = '';
      try {
        const { data } = await axios.post(`/api/gmail/mensajes/${m.id}/resumen`, {}, { timeout: 60000 });
        resumenes.value = { ...resumenes.value, [m.id]: data.resumen };
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo resumir el correo.';
      } finally {
        resumiendoId.value = null;
      }
    }

    async function guardarEnRaindrop() {
      const id = detalle.value?.id;
      if (!id || guardandoRaindrop.value) return;
      if (!status.value?.raindropConfigured) {
        error.value = 'Configura Raindrop en Perfil para guardar correos.';
        return;
      }
      guardandoRaindrop.value = true;
      error.value = '';
      mensaje.value = '';
      try {
        const { data } = await axios.post(`/api/gmail/mensajes/${id}/raindrop`, {}, { timeout: 60000 });
        mensaje.value = data.message || 'Correo guardado en Raindrop.';
        if (data.url) {
          window.open(data.url, '_blank', 'noopener');
        }
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo guardar en Raindrop.';
      } finally {
        guardandoRaindrop.value = false;
      }
    }

    async function preguntarCorreo() {
      const text = preguntaCorreo.value.trim();
      if (!text || !detalle.value?.id) return;
      preguntandoCorreo.value = true;
      const id = detalle.value.id;
      const prev = chatsCorreo.value[id] || [];
      const next = [...prev, { role: 'user', content: text }];
      chatsCorreo.value = { ...chatsCorreo.value, [id]: next };
      preguntaCorreo.value = '';
      try {
        const { data } = await axios.post(
          `/api/gmail/mensajes/${id}/chat`,
          { message: text, history: prev },
          { timeout: 60000 }
        );
        chatsCorreo.value = {
          ...chatsCorreo.value,
          [id]: [...next, { role: 'assistant', content: data.reply }],
        };
        if ((data.toolsUsed || []).includes('archive_message') || (data.toolsUsed || []).includes('apply_safe_suggestions')) {
          await cargarInbox();
          await cargarStatus();
        }
      } catch (e) {
        error.value = e.response?.data?.error || 'El agente no pudo responder.';
      } finally {
        preguntandoCorreo.value = false;
      }
    }

    function textoSalida(tipo) {
      return tipo === 'papelera' ? 'Movido a la papelera' : 'Conversación archivada';
    }

    function mostrarAviso(op) {
      avisoAccion.value = { id: op.id, texto: textoSalida(op.tipo) };
      if (avisoTimer) clearTimeout(avisoTimer);
      avisoTimer = setTimeout(() => {
        if (avisoAccion.value?.id === op.id) avisoAccion.value = null;
      }, 5000);
    }

    function restaurarEnLista(op) {
      if (op.restoredLocal) return;
      op.restoredLocal = true;
      if (!messages.value.some((m) => m.id === op.id)) {
        messages.value = insertarEnIndice(messages.value, op.index, op.snapshot);
        counts.value = ajustarCountsAlRestaurar(counts.value, op.snapshot);
        inboxTotal.value += 1;
        if (esNoLeido(op.snapshot)) inboxUnread.value += 1;
        aplicarConteoBandeja({ inboxTotal: inboxTotal.value });
      }
    }

    async function revertirEnServidor(op) {
      if (op.reverted || op.revirtiendo || operaciones.get(op.id) !== op) return;
      op.revirtiendo = true;
      const accion = op.tipo === 'papelera' ? 'restaurar' : 'desarchivar';
      try {
        await axios.post(`/api/gmail/mensajes/${op.id}/accion`, {
          accion,
          unread: esNoLeido(op.snapshot),
        }, { timeout: 30000 });
        op.reverted = true;
      } finally {
        op.revirtiendo = false;
      }
    }

    async function enviarQuitar(op) {
      try {
        await axios.post(`/api/gmail/mensajes/${op.id}/accion`, { accion: op.tipo }, { timeout: 30000 });
      } catch (e) {
        op.settled = true;
        if (operaciones.get(op.id) !== op || op.undone) return;
        restaurarEnLista(op);
        if (!detalle.value) abrir(op.snapshot);
        error.value = e.response?.data?.error || 'No se pudo archivar. El correo sigue en la bandeja.';
        if (avisoAccion.value?.id === op.id) avisoAccion.value = null;
        return;
      }
      op.settled = true;
      if (!op.undone || operaciones.get(op.id) !== op) return;
      try {
        await revertirEnServidor(op);
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo deshacer. Pulsa Actualizar.';
      }
    }

    function quitarDeBandeja(tipo, actual) {
      if (!actual?.id) return;
      const idx = messages.value.findIndex((m) => m.id === actual.id);
      if (idx < 0) return;
      const snapshot = messages.value[idx];
      const siguiente = siguienteVisible(filtrados.value, actual.id);
      const eraElAbierto = seleccionado.value?.id === actual.id || detalle.value?.id === actual.id;

      messages.value = messages.value.filter((m) => m.id !== actual.id);
      counts.value = ajustarCountsAlQuitar(counts.value, snapshot);
      inboxTotal.value = Math.max(0, inboxTotal.value - 1);
      if (esNoLeido(snapshot)) inboxUnread.value = Math.max(0, inboxUnread.value - 1);
      aplicarConteoBandeja({ inboxTotal: inboxTotal.value });

      if (eraElAbierto) {
        if (siguiente) abrir(siguiente);
        else {
          seleccionado.value = null;
          detalle.value = null;
        }
      }

      const op = {
        id: actual.id,
        tipo,
        index: idx,
        snapshot,
        undone: false,
        settled: false,
        restoredLocal: false,
        reverted: false,
      };
      operaciones.set(op.id, op);
      mostrarAviso(op);
      enviarQuitar(op);
      if (!messages.value.length) reponerBandeja();
    }

    async function reponerBandeja() {
      if (reponiendo.value || messages.value.length > 0) return;
      reponiendo.value = true;
      try {
        const { data } = await axios.get('/api/gmail/inbox', {
          params: { max: INBOX_LIST_PAGE },
          timeout: 90000,
        });
        if (messages.value.length > 0) return;
        const ocultos = new Set(
          [...operaciones.values()]
            .filter((op) => !op.undone && !op.restoredLocal)
            .map((op) => op.id)
        );
        aplicarPaginaBandeja({
          ...data,
          messages: (data.messages || []).filter((item) => !ocultos.has(item.id)),
        });
        if (!detalle.value && messages.value[0]) abrir(messages.value[0]);
      } catch {
        // La página cargada ya está vacía; Actualizar vuelve a leer Gmail.
      } finally {
        reponiendo.value = false;
      }
    }

    async function deshacerAccion() {
      const aviso = avisoAccion.value;
      if (!aviso) return;
      const op = operaciones.get(aviso.id);
      avisoAccion.value = null;
      if (!op || op.undone) return;
      op.undone = true;
      restaurarEnLista(op);
      if (!detalle.value) abrir(op.snapshot);
      if (!op.settled) return;
      try {
        await revertirEnServidor(op);
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo deshacer. Pulsa Actualizar.';
      }
    }

    function onTeclaBandeja(ev) {
      if (visorPdf.value || visorCargando.value) {
        if (ev.key === 'Escape') {
          ev.preventDefault();
          cerrarVisorPdf();
        }
        return;
      }
      const tag = ev.target?.tagName;
      const escribiendo = tag === 'INPUT' || tag === 'TEXTAREA' || ev.target?.isContentEditable;
      if (ev.key === 'F' && ev.shiftKey && !ev.metaKey && !ev.ctrlKey && !ev.altKey) {
        if (escribiendo) return;
        ev.preventDefault();
        alternarFocus();
        return;
      }
      if (focusActivo.value && !escribiendo && !ev.metaKey && !ev.ctrlKey && !ev.altKey) {
        if (ev.key === 'Escape') {
          ev.preventDefault();
          focusActivo.value = false;
          return;
        }
        if (ev.key === 'ArrowRight') {
          ev.preventDefault();
          focusIr(focusSiguiente.value);
          return;
        }
        if (ev.key === 'ArrowLeft') {
          ev.preventDefault();
          focusIr(focusAnterior.value);
          return;
        }
      }
      if (ev.key !== 'e' && ev.key !== 'E' && ev.key !== 'A') return;
      if (ev.metaKey || ev.ctrlKey || ev.altKey) return;
      if (escribiendo) return;
      if (tab.value !== 'bandeja' || !detalle.value?.id) return;
      ev.preventDefault();
      quitarDeBandeja('archivar', detalle.value);
    }

    async function accion(tipo) {
      if (!detalle.value?.id) return;
      if (tipo === 'archivar' || tipo === 'papelera') {
        quitarDeBandeja(tipo, detalle.value);
        return;
      }
      await aplicarAccion(detalle.value.id, tipo);
    }

    function archivarEmail(m) {
      quitarDeBandeja('archivar', m);
    }

    async function aplicarAccion(id, tipo) {
      if (!id || accionando.value) return;
      accionando.value = true;
      error.value = '';
      try {
        await axios.post(`/api/gmail/mensajes/${id}/accion`, { accion: tipo });
        if (tipo === 'leer') {
          const estaba = messages.value.find((m) => m.id === id);
          if (esNoLeido(estaba) || esNoLeido(detalle.value)) {
            counts.value = { ...counts.value, unread: Math.max(0, Number(counts.value.unread || 0) - 1) };
          }
          const marcar = (m) => ({
            ...m,
            unread: false,
            labelIds: (m.labelIds || []).filter((l) => l !== 'UNREAD'),
          });
          messages.value = messages.value.map((m) => (m.id === id ? marcar(m) : m));
          if (detalle.value?.id === id) detalle.value = marcar(detalle.value);
          if (seleccionado.value?.id === id) seleccionado.value = marcar(seleccionado.value);
        }
        if (tipo === 'estrella') {
          const marcar = (m) => ({
            ...m,
            starred: true,
            labelIds: (m.labelIds || []).includes('STARRED') ? m.labelIds : [...(m.labelIds || []), 'STARRED'],
          });
          messages.value = messages.value.map((m) => (m.id === id ? marcar(m) : m));
          if (detalle.value?.id === id) detalle.value = marcar(detalle.value);
        }
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo aplicar la acción.';
      } finally {
        accionando.value = false;
      }
    }

    async function crearBorrador() {
      if (!detalle.value) return;
      accionando.value = true;
      try {
        await axios.post(`/api/gmail/mensajes/${detalle.value.id}/accion`, {
          accion: 'borrador',
          to: detalle.value.fromEmail,
          subject: detalle.value.subject.startsWith('Re:') ? detalle.value.subject : `Re: ${detalle.value.subject}`,
          body: borrador.value,
          threadId: detalle.value.threadId,
        });
        mensaje.value = 'Borrador guardado en Gmail. Revísalo allí antes de enviar.';
        borrador.value = '';
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo crear el borrador.';
      } finally {
        accionando.value = false;
      }
    }

    async function cargarChat() {
      try {
        const { data } = await axios.get('/api/gmail/chat/history');
        chat.value = data.messages || [];
      } catch {
        chat.value = [];
      }
    }

    async function enviarAgente() {
      const text = pregunta.value.trim();
      if (!text) return;
      preguntando.value = true;
      chat.value = [...chat.value, { temp: Date.now(), role: 'user', content: text }];
      pregunta.value = '';
      try {
        const { data } = await axios.post('/api/gmail/agente', {
          message: text,
          messageId: detalle.value?.id || undefined,
        }, { timeout: 60000 });
        chat.value = [...chat.value, { temp: Date.now() + 1, role: 'assistant', content: data.reply }];
        if ((data.toolsUsed || []).includes('apply_safe_suggestions') || (data.toolsUsed || []).includes('archive_message')) {
          await cargarInbox();
          await cargarStatus();
        }
      } catch (e) {
        error.value = e.response?.data?.error || 'El agente no pudo responder.';
      } finally {
        preguntando.value = false;
      }
    }

    async function borrarChat() {
      await axios.delete('/api/gmail/chat/history');
      chat.value = [];
    }

    async function cargarReglas() {
      try {
        const { data } = await axios.get('/api/gmail/reglas');
        reglas.value = data.reglas || [];
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudieron cargar las reglas.';
      }
    }

    async function cargarPushSetup() {
      try {
        const { data } = await axios.get('/api/gmail/push-setup');
        pushSetup.value = data;
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo leer la config de Pub/Sub.';
      }
    }

    async function activarWatch() {
      activandoWatch.value = true;
      error.value = '';
      try {
        const { data } = await axios.post('/api/gmail/watch', {}, { timeout: 30000 });
        pushSetup.value = data;
        if (data.watching || data.result?.ok) {
          mensaje.value = 'Watch de Gmail activado. El webhook ya puede recibir correos.';
        } else if (data.result?.skipped) {
          mensaje.value = 'Watch ya estaba vigente.';
        }
        await cargarStatus();
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo activar el watch. Crea el topic Pub/Sub y reintenta.';
        if (e.response?.data?.topicName) pushSetup.value = e.response.data;
      } finally {
        activandoWatch.value = false;
      }
    }

    async function copiar(texto) {
      const value = String(texto || '');
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        mensaje.value = 'Copiado.';
      } catch {
        error.value = 'No se pudo copiar. Selecciona el texto a mano.';
      }
    }

    async function guardarRegla() {
      try {
        await axios.post('/api/gmail/reglas', nuevaRegla.value);
        nuevaRegla.value = { tipo: 'dominio', valor: '', accion: 'archivar' };
        await cargarReglas();
        await cargarInbox();
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo guardar la regla.';
      }
    }

    async function borrarRegla(id) {
      try {
        await axios.delete(`/api/gmail/reglas/${id}`);
        await cargarReglas();
        await cargarInbox();
      } catch (e) {
        error.value = e.response?.data?.error || 'No se pudo borrar la regla.';
      }
    }

    watch(cargando, async (estaCargando) => {
      if (estaCargando) return;
      await nextTick();
      encajarAnchoLista();
    });

    watch(() => route.query.filtro, (v) => { if (v === 'sinleer') leerQuery(); });

    onMounted(async () => {
      window.addEventListener('keydown', onTeclaBandeja);
      window.addEventListener('resize', encajarAnchoLista);
      leerQuery();
      try {
        await cargarOauthConfig();
        await cargarStatus();
        if (status.value?.connected) {
          await cargarInbox();
          await cargarPushSetup();
        }
      } catch (e) {
        error.value = errorGmail(e, 'No se pudo cargar el agente Gmail.');
      }
    });

    let liveReloadTimer = null;
    watch(gmailInboxTick, () => {
      if (!status.value?.connected) return;
      if (liveReloadTimer) clearTimeout(liveReloadTimer);
      liveReloadTimer = setTimeout(() => {
        if (!cargando.value) cargarInbox();
      }, 900);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('keydown', onTeclaBandeja);
      window.removeEventListener('resize', encajarAnchoLista);
      document.body.style.overflow = '';
      if (avisoTimer) clearTimeout(avisoTimer);
      if (liveReloadTimer) clearTimeout(liveReloadTimer);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      cerrarVisorPdf();
    });

    return {
      panelIA,
      preguntaCorreoEl,
      togglePanelIA,
      status,
      oauthConfig,
      conectando,
      cargando,
      cambiandoPausa,
      mostrarHorario,
      guardandoHorario,
      diasSemana,
      horarioForm,
      horarioActivo,
      abrirHorario,
      excepciones,
      nuevaExcepcion,
      guardandoExcepcion,
      anadirExcepcion,
      quitarExcepcion,
      anadirTramo,
      quitarTramo,
      copiarTramos,
      guardarHorario,
      pausaActiva,
      pausaDesde,
      pausaHasta,
      formatearHora,
      horaPausa,
      horaFinPausa,
      alternarPausa,
      aplicando,
      accionando,
      guardandoRaindrop,
      cuerpoCargando,
      avisoAccion,
      reponiendo,
      error,
      mensaje,
      mensajeOAuth,
      mensajeOAuthOk,
      messages,
      counts,
      seleccionado,
      detalle,
      tab,
      filtro,
      filtros,
      filtrados,
      bandejaTotal,
      bandejaUnread,
      quedanPorCargar,
      pendientesSiguientes,
      nextPageToken,
      cargandoMas,
      cargarSiguientes,
      chat,
      pregunta,
      preguntando,
      reglas,
      nuevaRegla,
      borrador,
      etiquetaAgente,
      etiquetaPush,
      fechaWatch,
      pushSetup,
      activandoWatch,
      cargarPushSetup,
      activarWatch,
      copiar,
      resumenActual,
      chatCorreo,
      adjuntosVisibles,
      descargandoId,
      visorPdf,
      visorCargando,
      esPdfAdjunto,
      iconoAdjunto,
      tamanoAdjunto,
      abrirAdjunto,
      cerrarVisorPdf,
      descargarDesdeVisor,
      resumiendoId,
      deshacerAccion,
      preguntaCorreo,
      preguntandoCorreo,
      fechaDetalle,
      fechaLista,
      fechaListaTitulo,
      workspaceEl,
      arrastrandoAncho,
      estiloLista,
      iniciarArrastreAncho,
      moverArrastreAncho,
      soltarArrastreAncho,
      ajustarAnchoLista,
      resetAnchoLista,
      badgeClase,
      ajustarFrame,
      ajustarFrameFocus,
      focusActivo,
      focusAnterior,
      focusSiguiente,
      focusPosicion,
      focusIr,
      alternarFocus,
      conectar,
      desconectar,
      cargarInbox,
      aplicarSeguras,
      abrir,
      resumirEmail,
      guardarEnRaindrop,
      archivarEmail,
      preguntarCorreo,
      accion,
      crearBorrador,
      cargarChat,
      enviarAgente,
      borrarChat,
      cargarReglas,
      guardarRegla,
      borrarRegla,
    };
  },
};
</script>

<style scoped>
.gmail-topbar h2 {
  font-weight: 650;
}
.gmail-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}
.gmail-stat {
  border: 1px solid var(--linea, #dee2e6);
  background: #fff;
  border-radius: 999px;
  padding: 0.2rem 0.7rem;
  font-size: 0.8rem;
  line-height: 1.2;
  color: inherit;
}
.gmail-stat strong {
  font-size: 0.92rem;
  margin-right: 0.15rem;
}
.gmail-stat-sub {
  color: var(--tinta-suave, #6c757d);
  margin-left: 0.25rem;
}
.gmail-stat.active {
  background: #1c241c;
  color: #fff;
  border-color: #1c241c;
}
.gmail-stat.active .gmail-stat-sub,
.gmail-stat.active strong {
  color: inherit;
}
.gmail-stats-zero .gmail-stat:first-child {
  background: #d1e7dd;
  border-color: #badbcc;
}
.gmail-workspace {
  display: flex;
  align-items: stretch;
  min-width: 0;
}
.gmail-list-pane {
  flex: 0 0 auto;
  min-width: 0;
  max-width: 100%;
}
.gmail-reader-pane {
  flex: 1 1 auto;
  min-width: 0;
}
.gmail-splitter {
  flex: 0 0 10px;
  margin: 0 2px;
  cursor: col-resize;
  touch-action: none;
  position: relative;
  border-radius: 4px;
}
.gmail-splitter::before {
  content: '';
  position: absolute;
  top: 0.35rem;
  bottom: 0.35rem;
  left: 4px;
  width: 2px;
  border-radius: 2px;
  background: #d5dbe1;
}
.gmail-splitter:hover::before,
.gmail-splitter:focus-visible::before,
.gmail-arrastrando .gmail-splitter::before {
  background: #5c6770;
}
.gmail-splitter:focus-visible {
  outline: 2px solid #8ab4f8;
  outline-offset: 1px;
}
.gmail-list-scroll {
  position: relative;
  max-height: calc(100vh - 210px);
  overflow: auto;
}
.gmail-row-move {
  transition: transform 0.16s ease;
}
.gmail-row-leave-active {
  position: absolute;
  width: 100%;
  z-index: 1;
  transition: opacity 0.14s ease, transform 0.14s ease;
}
.gmail-row-leave-to {
  opacity: 0;
  transform: translateX(18px);
}
.gmail-undo {
  position: fixed;
  left: 50%;
  bottom: 1.25rem;
  transform: translateX(-50%);
  z-index: 1080;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  min-width: min(420px, calc(100vw - 2rem));
  background: #323232;
  color: #fff;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.28);
}
.gmail-undo button {
  background: none;
  border: 0;
  color: #8ab4f8;
  font-weight: 650;
  padding: 0;
}
.gmail-row {
  cursor: pointer;
}
.gmail-row-from,
.gmail-row-subject {
  font-weight: 400;
}
.gmail-row:not(.gmail-row-unread):not(.active) {
  background-color: #f2f6fc;
  color: #5f6368;
}
.gmail-row:not(.gmail-row-unread):not(.active) .gmail-row-snippet {
  color: #80868b;
}
.gmail-row-unread:not(.active) {
  background-color: #fff;
}
.gmail-row-unread .gmail-row-from,
.gmail-row-unread .gmail-row-subject {
  font-weight: 700;
  color: #202124;
}
.gmail-row-snippet {
  color: var(--tinta-suave, #6c757d);
  min-width: 0;
}
.gmail-row-date {
  font-size: 0.68rem;
  font-weight: 400;
  line-height: 1.2;
  color: #8b949e;
  letter-spacing: 0.01em;
  white-space: nowrap;
}
.gmail-row.active .gmail-row-snippet {
  color: inherit;
  opacity: 0.85;
}
.gmail-row.active .gmail-row-date {
  color: rgba(255, 255, 255, 0.82);
}
.gmail-row.active .btn-outline-secondary {
  border-color: rgba(255, 255, 255, 0.75);
  color: inherit;
}
.gmail-reader {
  overflow: hidden;
}
.gmail-reader-layout {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}
.gmail-reader-layout > .gmail-reader {
  min-width: 0;
}
@media (min-width: 992px) {
  .gmail-reader-layout {
    flex-direction: row;
    align-items: flex-start;
  }
  .gmail-reader-layout > .gmail-reader {
    flex: 1 1 auto;
  }
  .gmail-ia-panel {
    display: none;
    flex: 0 0 340px;
    width: 340px;
    position: sticky;
    top: 0.75rem;
    max-height: calc(100vh - 1.5rem);
    overflow: auto;
  }
  .gmail-ia-open .gmail-ia-panel {
    display: block;
  }
  .gmail-ia-panel .gmail-chat-mini {
    max-height: calc(100vh - 380px);
    min-height: 120px;
  }
  .gmail-ia-panel .gmail-borrador {
    min-height: 140px;
  }
}
@media (max-width: 991.98px) {
  .gmail-workspace {
    flex-direction: column;
    gap: 0.5rem;
  }
  .gmail-list-pane {
    width: 100% !important;
  }
  .gmail-splitter {
    display: none;
  }
}
.gmail-reader-scroll {
  max-height: calc(100vh - 210px);
  overflow: auto;
}
.gmail-frame {
  display: block;
  width: 100%;
  min-height: 360px;
  height: calc(100vh - 280px);
  border: 1px solid var(--linea, #dee2e6);
  border-radius: 0.5rem;
  background: #fff;
  color-scheme: light;
  overflow: auto;
}
.gmail-attachments {
  background: #f8f9fa;
  border: 1px solid var(--linea, #dee2e6);
  border-radius: 0.5rem;
  padding: 0.55rem 0.75rem;
}
.gmail-att-btn {
  max-width: 100%;
  display: inline-flex;
  align-items: center;
}
.gmail-att-name {
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gmail-pdf-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(32, 33, 36, 0.72);
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0.75rem;
}
.gmail-pdf-dialog {
  display: flex;
  flex-direction: column;
  width: min(1100px, 100%);
  height: 100%;
  background: #202124;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
}
.gmail-pdf-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.55rem 0.75rem;
  background: #303134;
  color: #fff;
}
.gmail-pdf-title {
  display: flex;
  align-items: center;
  min-width: 0;
  font-weight: 600;
}
.gmail-pdf-body {
  flex: 1;
  min-height: 0;
  background: #525659;
  position: relative;
}
.gmail-pdf-frame {
  display: block;
  width: 100%;
  height: 100%;
  border: 0;
  background: #525659;
}
.gmail-pdf-loading {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #eee;
}
.gmail-summary {
  background: #f4f7f4;
  border: 1px solid var(--linea, #dee2e6);
  border-radius: 0.5rem;
  padding: 0.6rem 0.75rem;
}
.gmail-summary-text {
  white-space: pre-wrap;
}
.gmail-chat-mini {
  max-height: 180px;
  overflow: auto;
}
.gmail-focus {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  background: var(--bs-body-bg, #fff);
}
.gmail-focus-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid var(--linea, #dee2e6);
}
.gmail-focus-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 2rem 1rem 4rem;
}
.gmail-focus-article {
  max-width: 760px;
  margin: 0 auto;
}
.gmail-focus-frame {
  display: block;
  width: 100%;
  min-height: 280px;
  border: 0;
  border-radius: 0.5rem;
  background: #fff;
  color-scheme: light;
}
.gmail-focus-text {
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 1.05rem;
  line-height: 1.7;
}
.gmail-body {
  white-space: pre-wrap;
  max-height: 480px;
  overflow: auto;
  background: #fff;
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--linea, #dee2e6);
}
.gmail-chat {
  max-height: 420px;
  overflow: auto;
}
.gmail-bubble {
  white-space: pre-wrap;
  padding: 0.6rem 0.8rem;
  border-radius: 0.5rem;
}
.gmail-bubble-user {
  background: #e7f1ff;
}
.gmail-bubble-agent {
  background: #f1f3f5;
}
</style>
